import {useParams} from 'react-router-dom'
import {Table, Space} from 'antd'

import styles from './SecurityItems.module.scss'
import {useGetRequest} from 'modules/request'
import {
  Box,
  Text,
  Button,
  Modal,
  UserAvatar,
  IconButton,
  ArrowIcon,
  HouseExchangeSecondIcon,
} from 'common/components'
import {useOpen} from 'common/hooks'
import {
  totalNumberFromArray,
  formatMoney,
  maybePluralize,
  formatDate,
} from 'common/helpers'
import {SecurityItemForm, useDischargeSecurityItem, SecurityItem} from '..'

interface SecurityItemsProps {
  items: SecurityItem[]
  isRequest?: boolean
}

interface AddItemProps {
  projectUuid: string
  request: any
}

interface EditItemProps {
  projectUuid: string
  item: SecurityItem
}

interface DischargedItemsProps {
  items: SecurityItem[]
}

export default function SecurityItems({
  items,
  isRequest = false,
}: SecurityItemsProps) {
  const params = useParams()
  const {data: request} = useGetRequest(params.uuid, isRequest)
  const projectUuid = isRequest ? request.project.uuid : params.uuid

  const activeItems = items.filter((item) => item.status !== 'discharged')
  const dischargedItems = items.filter((item) => item.status === 'discharged')

  const data = activeItems.map((item, index) => ({
    ...item,
    key: item.uuid,
    number: index + 1,
  }))

  const columns: any[] = [
    {
      title: '#',
      dataIndex: 'number',
      render: (value: string) => (
        <Text color="grey" size="xs">
          {value}
        </Text>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      render: (value: string) => <Text weight="demiBold">{value}</Text>,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      render: (value: string, record: SecurityItem) => (
        <Text color="grey" size="xs">
          {formatDate(value, 'fifth')}
          {record.createdBy ? ` by ${record.createdBy.firstName}` : ''}
        </Text>
      ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      render: (value: string) => (
        <Text color="grey" size="xs">
          {value}
        </Text>
      ),
    },
    {
      title: (
        <Space split="|">
          <span>Land</span>
          <span>Improvement</span>
        </Space>
      ),
      align: 'center',
      render: (_: any, record: SecurityItem) => (
        <Box size="xs">
          <Space
            split={
              <Box as="span" color="grey">
                |
              </Box>
            }
          >
            <span>{formatMoney(record.landValue, {cents: true})}</span>
            <span>{formatMoney(record.improvementValue, {cents: true})}</span>
          </Space>
        </Box>
      ),
    },
    {
      title: 'Security value',
      align: 'right',
      render: (_: any, record: SecurityItem) => (
        <Text size="xs" weight="demiBold">
          {formatMoney(record.landValue + record.improvementValue, {
            cents: true,
          })}
        </Text>
      ),
    },
    {
      align: 'right',
      render: (_: any, record: SecurityItem) => (
        <EditItem projectUuid={projectUuid} item={record} />
      ),
    },
  ]

  return (
    <>
      <Table pagination={false} columns={columns} dataSource={data} />
      <div className={styles.footer}>
        <Space size={12}>
          <Space size={6}>
            <HouseExchangeSecondIcon />
            <Text as="span" weight="demiBold">
              {maybePluralize(activeItems.length, 'item')}
            </Text>
          </Space>
          <AddItem projectUuid={projectUuid} request={request} />
        </Space>
        <Space size={32}>
          <Box weight="medium" align="right">
            <Text size="3xl" mb={2}>
              {formatMoney(
                totalNumberFromArray(
                  activeItems.map(
                    (item) => item.improvementValue + item.landValue
                  )
                ),
                {cents: true}
              )}
            </Text>
            <Text size="xs" color="grey">
              Security value
            </Text>
          </Box>
        </Space>
      </div>
      {Boolean(dischargedItems.length) && (
        <DischargedItems items={dischargedItems} />
      )}
    </>
  )
}

const AddItem = ({projectUuid, request}: AddItemProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()

  return (
    <>
      <Button variant="secondary" onClick={handleOpen}>
        add item
      </Button>
      <Modal
        title="New security item"
        width={486}
        open={isOpen}
        onCancel={handleClose}
      >
        <SecurityItemForm
          projectUuid={projectUuid}
          request={request}
          onClose={handleClose}
        />
      </Modal>
    </>
  )
}

const EditItem = ({projectUuid, item}: EditItemProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen()
  const {
    mutate: dischargeSecurityItem,
    isMutating: isDischargeSecurityItemMutating,
  } = useDischargeSecurityItem(projectUuid, item.uuid, handleClose)

  const handleDischarge = () => dischargeSecurityItem()

  return (
    <>
      <IconButton onClick={handleOpen}>
        <ArrowIcon className={styles.icon} width={11} height="100%" />
      </IconButton>
      <Modal
        title={
          <div className={styles.edit}>
            <p>Security item</p>
            <Button
              variant="secondary"
              loading={isDischargeSecurityItemMutating}
              onClick={handleDischarge}
            >
              discharge
            </Button>
          </div>
        }
        width={486}
        open={isOpen}
        onCancel={handleClose}
      >
        <SecurityItemForm
          projectUuid={projectUuid}
          item={item}
          onClose={handleClose}
        />
      </Modal>
    </>
  )
}

const DischargedItems = ({items}: DischargedItemsProps) => {
  const data = items.map((item) => ({
    ...item,
    key: item.uuid,
  }))

  const columns: any[] = [
    {
      dataIndex: 'updatedAt',
      render: (value: string, record: SecurityItem) => {
        const {user} = record.statusHistory?.find(
          (historyItem) => historyItem.status === 'discharged'
        )
        return (
          <Space>
            <UserAvatar size={26} src={user?.photo} alt={user?.firstName} />
            <Text weight="demiBold" size="xs">
              {formatDate(value, 'fifth')}
              {user && (
                <>
                  <br />
                  by {user.firstName}
                </>
              )}
            </Text>
          </Space>
        )
      },
    },
    {
      dataIndex: 'type',
      render: (value: string) => <Text weight="demiBold">{value}</Text>,
    },
    {
      dataIndex: 'createdAt',
      render: (value: string, record: SecurityItem) => (
        <Text color="grey" size="xs">
          {formatDate(value, 'fifth')}
          {record.createdBy ? ` by ${record.createdBy.firstName}` : ''}
        </Text>
      ),
    },
    {
      dataIndex: 'address',
      render: (value: string) => (
        <Text color="grey" size="xs">
          {value}
        </Text>
      ),
    },
    {
      align: 'right',
      render: (_: any, record: SecurityItem) => (
        <Box size="xs">
          <Space
            split={
              <Box as="span" color="grey">
                |
              </Box>
            }
          >
            <span>{formatMoney(record.landValue, {cents: true})}</span>
            <span>{formatMoney(record.improvementValue, {cents: true})}</span>
            <Text as="span" weight="demiBold">
              {formatMoney(record.landValue + record.improvementValue, {
                cents: true,
              })}
            </Text>
          </Space>
        </Box>
      ),
    },
  ]

  return (
    <div className={styles.discharged}>
      <h3 className={styles.subTitle}>Discharged items</h3>
      <Table
        pagination={false}
        showHeader={false}
        columns={columns}
        dataSource={data}
      />
    </div>
  )
}
